import * as React from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Inbox, Lock, Mail, ShieldAlert } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ALLOWED_DOMAIN, DEV_AUTH_BYPASS, isAllowedEmail, sendMagicLink } from "@/lib/auth";
import { useAuth } from "@/lib/AuthContext";

export function Login() {
  const { user, loading, forbidden } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const returnTo = params.get("returnTo") ?? "/";

  const [email, setEmail] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (DEV_AUTH_BYPASS) nav(returnTo, { replace: true });
  }, [nav, returnTo]);

  if (!loading && user) {
    return <Navigate to={returnTo} replace />;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!isAllowedEmail(email)) {
      setErr(`This dashboard is restricted to Ultraviolet team members (${ALLOWED_DOMAIN}).`);
      return;
    }
    setSending(true);
    const { error } = await sendMagicLink(email);
    setSending(false);
    if (error) setErr(error);
    else setSent(true);
  };

  return (
    <div className="grid min-h-screen place-items-center bg-background p-6">
      <Card className="w-full max-w-sm border-border/80 shadow-soft">
        <CardHeader className="items-start gap-1.5">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-brand text-brand-foreground">
            <Inbox className="h-4 w-4" />
          </div>
          <CardTitle className="mt-1">Kitt</CardTitle>
          <CardDescription>
            Internal admin dashboard for Ultraviolet. Sign in with your{" "}
            <span className="font-mono">{ALLOWED_DOMAIN}</span> email.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {forbidden ? (
            <div className="mb-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
              <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Your email isn't allowlisted. Sign out and try again with an Ultraviolet
                address.
              </span>
            </div>
          ) : null}

          {sent ? (
            <div className="flex items-start gap-2 rounded-md border border-success/40 bg-success/10 p-3 text-xs text-success">
              <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Check your inbox. We sent a magic link to <b>{email}</b>. The link will sign
                you in and bring you back here.
              </span>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-3">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={`you${ALLOWED_DOMAIN}`}
                  className="mt-1"
                  autoComplete="email"
                  autoFocus
                />
              </div>
              {err ? (
                <p className="text-2xs text-destructive">{err}</p>
              ) : (
                <p className="text-2xs text-muted-foreground">
                  We'll send a one-time link. No password.
                </p>
              )}
              <Button type="submit" disabled={sending} className="w-full">
                <Mail className="mr-1.5 h-3.5 w-3.5" />
                {sending ? "sending…" : "Send magic link"}
              </Button>
            </form>
          )}

          <div className="mt-4 flex items-center gap-1.5 text-2xs text-muted-foreground">
            <Lock className="h-3 w-3" />
            <span>Server enforces the same allowlist. JWT-bound sessions.</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
